/** Interactive search budgets must propagate to RPC and DB work, not only UI timers. */
export const INTERACTIVE_SEARCH_TIMEOUT_MS = 5_000;

export class SearchDeadlineExceeded extends Error {
  readonly code = 'SEARCH_TIMEOUT' as const;
  constructor(readonly stage: string) {
    super('Search took too long. Please try again.');
    this.name = 'SearchDeadlineExceeded';
  }
}

export function clampSearchDeadline(value?: number, now = Date.now()): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.min(value, now + INTERACTIVE_SEARCH_TIMEOUT_MS)
    : now + INTERACTIVE_SEARCH_TIMEOUT_MS;
}

export function remainingSearchTime(deadlineAt: number, stage: string, now = Date.now()): number {
  const remaining = Math.floor(deadlineAt - now);
  if (remaining <= 0) throw new SearchDeadlineExceeded(stage);
  return remaining;
}

/** Do not use this around writes: a timer cannot roll back a remote mutation. */
export async function withinSearchDeadline<T>(
  deadlineAt: number,
  stage: string,
  action: () => Promise<T>,
  stageLimitMs = INTERACTIVE_SEARCH_TIMEOUT_MS,
): Promise<T> {
  const timeoutMs = Math.min(remainingSearchTime(deadlineAt, stage), stageLimitMs);
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      Promise.resolve().then(() => {
        remainingSearchTime(deadlineAt, stage);
        return action();
      }),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new SearchDeadlineExceeded(stage)), timeoutMs);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** Shared crypto also serves mutations; only read callers opt into a timer. */
export function withinOptionalSearchDeadline<T>(
  deadlineAt: number | undefined,
  stage: string,
  action: () => Promise<T>,
): Promise<T> {
  return deadlineAt === undefined ? action() : withinSearchDeadline(deadlineAt, stage, action);
}
